import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Canvas, useFrame } from '@react-three/fiber';
import { Sphere, MeshDistortMaterial } from '@react-three/drei';
import * as THREE from 'three';
import Particles from '../../lib/components/ui/special/Particles';
import { Button } from '../../lib/components/ui/button';
import RobotImage from '/assets/RobotNotFound.png';

// Floating Blob Component
interface FloatingBlobProps {
  position: [number, number, number];
  color: string;
  scale?: number;
}

const FloatingBlob: React.FC<FloatingBlobProps> = ({ position, color, scale = 1 }) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);

  useFrame((state) => {
    if (meshRef.current) {
      // Gentle floating animation
      meshRef.current.position.y = position[1] + Math.sin(state.clock.elapsedTime * 2) * 0.1;
      meshRef.current.position.x = position[0] + Math.sin(state.clock.elapsedTime * 1.5) * 0.05;

      // Mouse interaction - subtle attraction
      const mouse = state.mouse;
      const mouseInfluence = 0.1;
      meshRef.current.rotation.x += (mouse.y * mouseInfluence - meshRef.current.rotation.x) * 0.02;
      meshRef.current.rotation.y += (mouse.x * mouseInfluence - meshRef.current.rotation.y) * 0.02;

      // Scale animation when hovered
      const targetScale = hovered ? scale * 1.2 : scale;
      meshRef.current.scale.lerp(new THREE.Vector3(targetScale, targetScale, targetScale), 0.1);
    }
  });

  return (
    <Sphere
      ref={meshRef}
      args={[0.5, 32, 32]}
      position={position}
      scale={scale}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      <MeshDistortMaterial
        color={color}
        attach="material"
        distort={0.4}
        speed={2}
        roughness={0}
        metalness={0.1}
        transparent
        opacity={0.8}
      />
    </Sphere>
  );
};

// Blobs Scene Component
const BlobsScene = () => {
  return (
    <Canvas camera={{ position: [0, 0, 5] }} style={{ position: 'absolute', inset: 0 }}>
      <ambientLight intensity={0.5} />
      <pointLight position={[10, 10, 10]} intensity={1} />
      <pointLight position={[-10, -10, -10]} intensity={0.5} />

      {/* Floating Blobs */}
      <FloatingBlob position={[2, 1, 0]} color="#60a5fa" scale={0.8} />
      <FloatingBlob position={[-2.5, -0.5, 0]} color="#a855f7" scale={0.6} />
      <FloatingBlob position={[1.5, -1.5, 0]} color="#06b6d4" scale={0.5} />
      <FloatingBlob position={[-1.8, 1.8, 0]} color="#8b5cf6" scale={0.7} />
      <FloatingBlob position={[3, -0.8, 0]} color="#3b82f6" scale={0.4} />
      <FloatingBlob position={[-3.2, 0.5, 0]} color="#ec4899" scale={0.6} />
    </Canvas>
  );
};

const NotFound = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/');
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-white">
      {/* Particles Background */}
      <Particles
        className="absolute inset-0 z-10"
        quantity={60}
        staticity={40}
        ease={50}
        size={0.8}
        color="#888888"
        vx={0.1}
        vy={0.1}
      />

      {/* Main Content */}
      <div className="relative z-20 mx-auto flex max-w-6xl flex-col items-center justify-center gap-8 px-6 lg:flex-row lg:gap-16">
        {/* Left Side - Text Content */}
        <div className="flex flex-col items-center space-y-6 text-center lg:flex-1 lg:items-start lg:text-left">
          {/* 404 Number */}
          <div className="relative">
            <h1 className="text-8xl leading-none font-bold text-gray-900 lg:text-9xl">404</h1>
            <div className="absolute right-0 -bottom-2 left-0 h-1 rounded-full bg-gradient-to-r from-blue-500 to-purple-600"></div>
          </div>

          {/* Error Message */}
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-gray-800 lg:text-3xl">Ώπα! Ξέμεινες από οξυγόνο!</h2>
            <p className="max-w-md text-lg text-gray-600">Η σελίδα που ψάχνεις δεν υπάρχει ή έχει μεταφερθεί</p>
          </div>

          {/* Back Button */}
          <div className="z-50 pt-4">
            <Button
              onClick={handleGoBack}
              className="transform rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 px-8 py-3 font-medium text-white shadow-lg transition-all duration-300 hover:scale-105 hover:from-blue-600 hover:to-purple-700 hover:shadow-xl"
            >
              Πάμε πίσω
            </Button>
          </div>
        </div>

        {/* Right Side - Robot Image */}
        <div className="flex flex-1 justify-center lg:justify-end">
          <div className="relative">
            {/* Robot Image - No bouncing */}
            <div className="relative z-10">
              <img
                src={RobotImage}
                alt="Robot Not Found"
                className="h-auto w-80 object-contain drop-shadow-2xl filter lg:w-96"
              />
            </div>

            {/* 3D Floating Blobs around Robot */}
            <div className="absolute inset-0 h-full w-full">
              <BlobsScene />
            </div>
          </div>
        </div>
      </div>

      {/* Additional Floating Elements */}
      <div className="absolute top-20 left-20 h-16 w-16 animate-spin rounded-full border-2 border-gray-200 opacity-20"></div>
      <div className="absolute right-20 bottom-20 h-12 w-12 animate-pulse rounded-full border-2 border-gray-300 opacity-30"></div>
      <div className="absolute top-1/2 left-10 h-8 w-8 animate-bounce rounded-full border border-gray-200 opacity-25"></div>

      {/* Gradient Overlay for depth */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-gray-50/30"></div>
    </div>
  );
};

export default NotFound;
